import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useState } from 'react'

interface IProps {
  pageTitle?: string
  submitButtonLabel?: string
  hideSubmitButton?: boolean
  onSubmit?: () => Promise<void> | void
  onBack?: () => void
}

export const RailVisitPlanningPageHeader: FC<IProps> = observer(
  ({ pageTitle, submitButtonLabel, hideSubmitButton, onSubmit, onBack }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { appViewStore } = usePlanningStore()

    const [isSaving, setIsSaving] = useState(false)

    const handleSaveRailVisit = useCallback(async () => {
      try {
        appViewStore.showLoader()
        setIsSaving(true)

        if (onSubmit) {
          await onSubmit()
        }
      } catch (error) {
        appViewStore.setShowAlert(
          'error',
          t('railVisitCreationFailed', 'Rail Visit Creation Failed'),
        )
      } finally {
        appViewStore.hideLoader()
        setIsSaving(false)
      }
    }, [appViewStore, onSubmit, t])

    return (
      <Stack
        paddingX={theme.customSpacing.l}
        paddingY={theme.customSpacing.l}
        marginBottom={theme.customSpacing.l}
        alignItems='center'
        direction='row'
        sx={{ placeContent: 'space-between' }}
        borderBottom={`1px solid ${theme.palette.divider}`}
      >
        <Stack alignItems='flex-start'>
          <Typography variant='h3' align='center'>
            {pageTitle ?? t('Create rail visit')}
          </Typography>
        </Stack>

        <Stack gap={theme.customSpacing.m} direction='row' alignItems='center'>
          <Button
            variant='text'
            onClick={onBack}
            color='inherit'
            data-cy='rail-visit-header-cancel-btn'
          >
            {t('cancel', 'Cancel')}
          </Button>

          {!hideSubmitButton && (
            <Button
              disabled={isSaving}
              variant='contained'
              color='primary'
              onClick={handleSaveRailVisit}
              data-cy='rail-visit-planning-submit-button'
              type='button'
              startIcon={isSaving && <CircularProgress size='1rem' />}
            >
              {submitButtonLabel ?? t('submit', 'Submit')}
            </Button>
          )}
        </Stack>
      </Stack>
    )
  },
)
